import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';

const CharacterList = ({ characters, onEdit, onDelete }) => {
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(true);

  // Base URL for API (for debugging)
  const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:4000' 
    : '';

  const fetchFlows = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/flows/summary`);
      const data = await response.json();
      setFlows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading flows:', error);
    } finally {
      setFlowsLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  const getFlowLabel = (flowId) => {
    if (!flowId) return null;
    if (flowsLoading) return 'Loading…';
    const id = String(flowId);
    const flow = flows.find((f) => String(f._id) === id);
    if (flow?.name) return flow.name;
    return id.length > 10 ? `${id.slice(0, 8)}…` : id;
  };
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Character Name</TableCell>
            <TableCell>Instruction</TableCell>
            <TableCell>Start Flow</TableCell>
            <TableCell>Main Flow</TableCell>
            <TableCell>More Flow</TableCell>
            <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: 112 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {characters.map((character) => (
            <TableRow key={character._id} hover>
              <TableCell>
                <strong>{character.name}</strong>
              </TableCell>
              <TableCell>
                <div style={{ 
                  maxWidth: '300px', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {character.instruction}
                </div>
              </TableCell>
              <TableCell>
                {character.startFlowId ? (
                  <Chip 
                    label={getFlowLabel(character.startFlowId)} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                  />
                ) : (
                  <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8125rem' }}>None</Box>
                )}
              </TableCell>
              <TableCell>
                {character.mainFlowId ? (
                  <Chip 
                    label={getFlowLabel(character.mainFlowId)} 
                    size="small" 
                    color="secondary" 
                    variant="outlined"
                    sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                  />
                ) : (
                  <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8125rem' }}>None</Box>
                )}
              </TableCell>
              <TableCell>
                {character.moreFlowId ? (
                  <Chip 
                    label={getFlowLabel(character.moreFlowId)} 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                  />
                ) : (
                  <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8125rem' }}>None</Box>
                )}
              </TableCell>
              <TableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: 112 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.25
                  }}
                >
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={() => onEdit(character)}
                      color="primary"
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => onDelete(character._id)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {characters.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No characters found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CharacterList; 